/**
 * The /analytics surface (CR-25): the platform analytics dashboard over
 * GET /api/v1/analytics/summary. Totals cards for the period, daily bar charts (hand-rolled,
 * zero-dependency - the chart decision is recorded in the slice report), the top-rooms table
 * (the per-room substance the server orders and caps), the 7/30/90 range switcher and a
 * refresh action. Guests get the sign-in prompt; the SSO session, its auto-refresh and the
 * 401-recovery wrapper are the dashboard's shared machinery. The server backfills and caches
 * the aggregates; the page simply renders the range it asked for.
 *
 * Layout-root discipline: the page owns h-screen with internal scroll, exactly like the
 * webinars and recordings surfaces, and every color rides the dark theme tokens.
 */

import { ArrowLeft, CalendarPlus, ClipboardList, Film, RefreshCw, Timer, Users, Video, VideoIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../components/ui/button';
import { ANALYTICS_RANGES, AnalyticsApiError, fetchAnalyticsSummary, isAnalyticsRange } from '../api/analytics';
import type { AnalyticsDay, AnalyticsRange, AnalyticsSummary, AnalyticsTopRoomKind } from '../api/analytics';
import {
  browserSsoStorage,
  callWithSsoRecovery,
  loadSsoSession,
  startSsoAutoRefresh,
} from '../auth/sso';
import { showToast, ToastStack } from '../calendar/Toast';
import {
  checkSession,
  forgetSession,
  startCalendarSignIn,
  calendarSsoConfig,
} from '../calendar/signIn';
import { navigateTo } from '../lib/spa';
import type { KeyValueStorage } from '../room/devicePrefs';

/** The SPA path of this page (the dashboard sidebar's analytics entry leads here). */
export const ANALYTICS_PATH = '/analytics';

type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly summary: AnalyticsSummary }
  | { readonly kind: 'error' };

export interface AnalyticsPageProps {
  /** Injected for tests; defaults to the browser's shared SSO store (localStorage-backed). */
  readonly storage?: KeyValueStorage | null;
  /** Injected for tests; defaults to the global fetch. */
  readonly fetchImpl?: typeof fetch;
}

/** One totals card: the metric label, its period total and an icon. */
function TotalsCard(props: {
  readonly testId: string;
  readonly icon: JSX.Element;
  readonly label: string;
  readonly value: number;
  readonly format: (value: number) => string;
}): JSX.Element {
  return (
    <div
      data-testid={props.testId}
      className="rounded-xl border border-border bg-card p-4"
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        {props.icon}
        <span className="truncate text-[11.5px] font-medium">{props.label}</span>
      </div>
      <p className="mt-2 text-[22px] font-semibold leading-none tabular-nums text-foreground">
        {props.format(props.value)}
      </p>
    </div>
  );
}

/**
 * One daily bar chart row: a compact CSS bar per day (stacked segments when a split is
 * given), the tallest bar filling the lane. Zero-value days render a 2px stub so the lane
 * still reads as "a day passed with no activity". Pure divs - no chart library.
 */
function DailyBars(props: {
  readonly testId: string;
  readonly title: string;
  readonly days: readonly AnalyticsDay[];
  readonly value: (day: AnalyticsDay) => number;
  readonly split?: (day: AnalyticsDay) => { readonly primary: number; readonly secondary: number };
  readonly label: (day: AnalyticsDay) => string;
  readonly primaryClass: string;
  readonly secondaryClass: string;
}): JSX.Element {
  const max = Math.max(1, ...props.days.map(props.value));
  return (
    <section
      aria-label={props.title}
      data-testid={props.testId}
      className="rounded-xl border border-border bg-card p-4"
    >
      <h2 className="text-[13px] font-semibold text-foreground">{props.title}</h2>
      <div className="mt-3 flex h-28 items-end gap-1" role="img" aria-label={props.title}>
        {props.days.map((day) => {
          const total = props.value(day);
          const height = total === 0 ? 2 : Math.max(4, Math.round((total / max) * 100));
          const split = props.split?.(day) ?? { primary: total, secondary: 0 };
          const primaryShare =
            total === 0 || split.primary + split.secondary === 0
              ? 100
              : Math.round((split.primary / (split.primary + split.secondary)) * 100);
          return (
            <div
              key={day.date}
              data-testid={`${props.testId}-bar-${day.date}`}
              title={`${props.label(day)}: ${String(total)}`}
              className="flex min-w-0 flex-1 flex-col items-center gap-1"
            >
              <div
                className="flex w-full max-w-8 flex-col justify-end overflow-hidden rounded-sm"
                style={{ height: `${String(height)}%` }}
              >
                <div
                  className={props.secondaryClass}
                  style={{ height: `${String(100 - primaryShare)}%` }}
                />
                <div className={props.primaryClass} style={{ height: `${String(primaryShare)}%` }} />
              </div>
              <span className="w-full truncate text-center text-[9px] tabular-nums text-muted-foreground">
                {day.date.slice(8)}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * The room-kind badge of a top-rooms row; the colors mirror the meetings chart split
 * (sky = meeting, violet = webinar).
 */
function KindBadge(props: { readonly kind: AnalyticsTopRoomKind; readonly label: string }): JSX.Element {
  return (
    <span
      data-testid={`analytics-top-kind-${props.kind}`}
      className={
        'inline-flex items-center rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide ' +
        (props.kind === 'webinar' ? 'bg-violet-500/15 text-violet-300' : 'bg-sky-500/15 text-sky-300')
      }
    >
      {props.label}
    </span>
  );
}

export function AnalyticsPage({ storage, fetchImpl }: AnalyticsPageProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const store = useMemo(() => storage ?? browserSsoStorage(), [storage]);
  const nowMs = useCallback((): number => Date.now(), []);
  const doFetch = fetchImpl ?? globalThis.fetch;

  const [session, setSession] = useState<'signed_in' | 'guest'>(() =>
    store !== null && checkSession(store, new Date()) === 'signed_in' ? 'signed_in' : 'guest',
  );
  const [token, setToken] = useState<string | null>(() =>
    store !== null && checkSession(store, new Date()) === 'signed_in'
      ? (loadSsoSession(store)?.accessToken ?? null)
      : null,
  );
  const [range, setRange] = useState<AnalyticsRange>(7);
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const refreshSeqRef = useRef(0);
  const loadStartedRef = useRef(false);
  // The range the visible data was loaded for: a switch marks it stale until the fetch for
  // the new range resolves, so the header shows the honest state.
  const loadedRangeRef = useRef<AnalyticsRange>(7);

  const formatNumber = useCallback(
    (value: number): string => new Intl.NumberFormat(locale).format(value),
    [locale],
  );

  // A bearer the service rejected after the recovery wrapper: the rotation chain is dead.
  const handleUnauthorized = useCallback((): void => {
    if (store !== null) {
      forgetSession(store);
    }
    setToken(null);
    setSession('guest');
  }, [store]);

  useEffect(() => {
    if (store === null || session !== 'signed_in') {
      return undefined;
    }
    return startSsoAutoRefresh({
      config: calendarSsoConfig(),
      storage: store,
      fetchImpl: doFetch,
      nowMs,
      onRefreshed: (freshToken) => setToken(freshToken),
      onInvalidGrant: handleUnauthorized,
    });
  }, [store, doFetch, handleUnauthorized, nowMs, session]);

  const refresh = useCallback(
    (forRange: AnalyticsRange): void => {
      const currentToken = token;
      if (currentToken === null) {
        return;
      }
      const seq = refreshSeqRef.current + 1;
      refreshSeqRef.current = seq;
      callWithSsoRecovery(currentToken, (fresh) => fetchAnalyticsSummary(fresh, forRange, doFetch), {
        config: calendarSsoConfig(),
        storage: store,
        fetchImpl: doFetch,
        rejected: (err: unknown): boolean => err instanceof AnalyticsApiError && err.status === 401,
        onRefreshed: (freshToken) => setToken(freshToken),
        nowMs,
      })
        .then((summary) => {
          if (refreshSeqRef.current === seq) {
            loadedRangeRef.current = forRange;
            setLoad({ kind: 'ready', summary });
          }
        })
        .catch((err: unknown) => {
          if (refreshSeqRef.current !== seq) {
            return;
          }
          if (err instanceof AnalyticsApiError && err.status === 401) {
            handleUnauthorized();
            return;
          }
          showToast('error', t('analytics.error'));
          setLoad({ kind: 'error' });
        });
    },
    [token, doFetch, store, nowMs, handleUnauthorized, t],
  );

  // One load per signed-in mount (StrictMode double-mounts guarded by the ref).
  useEffect(() => {
    if (session !== 'signed_in' || loadStartedRef.current) {
      return;
    }
    loadStartedRef.current = true;
    refresh(range);
  }, [session, refresh, range]);

  const switchRange = useCallback(
    (next: AnalyticsRange): void => {
      if (next === range) {
        return;
      }
      setRange(next);
      refresh(next);
    },
    [range, refresh],
  );

  const handleSignIn = useCallback(async (): Promise<void> => {
    if (store === null) {
      showToast('error', t('sso.notice_storage_blocked'));
      return;
    }
    try {
      await startCalendarSignIn(
        calendarSsoConfig(),
        window.location.origin,
        store,
        window.location,
        ANALYTICS_PATH,
      );
    } catch {
      // staying on the page; the button retries
    }
  }, [store, t]);

  const summary = load.kind === 'ready' ? load.summary : null;
  const totals = summary?.totals;
  const days = summary?.days ?? [];
  const isEmpty =
    totals !== undefined &&
    totals.roomsCreated === 0 &&
    totals.meetingsHeld === 0 &&
    totals.webinarsHeld === 0 &&
    totals.participantSessions === 0 &&
    totals.participantMinutes === 0 &&
    totals.recordingsCount === 0 &&
    totals.invitesCreated === 0 &&
    totals.moderationActions === 0;

  const dayLabel = useCallback(
    (day: AnalyticsDay): string =>
      new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(
        new Date(`${day.date}T00:00:00Z`),
      ),
    [locale],
  );

  // The top-rooms last-activity stamp: UTC, like every analytics timestamp (the range note
  // says so); the date keeps the day readable, the time points into it.
  const lastActivityLabel = useCallback(
    (iso: string): string =>
      new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      }).format(new Date(iso)),
    [locale],
  );

  return (
    <div
      data-testid="analytics-page"
      className="flex h-screen flex-col overflow-hidden bg-background text-foreground"
    >
      <ToastStack />
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-card px-4 sm:px-6">
        <Button
          variant="ghost"
          size="icon"
          className="size-9 text-muted-foreground hover:text-foreground"
          aria-label={t('analytics.back_aria')}
          data-testid="analytics-back"
          onClick={() => navigateTo('/')}
        >
          <ArrowLeft aria-hidden className="size-4" />
        </Button>
        <h1 className="truncate text-[15px] font-semibold tracking-tight text-foreground">
          {t('analytics.page_title')}
        </h1>
        <div
          role="group"
          aria-label={t('analytics.range_aria')}
          data-testid="analytics-range"
          className="ml-2 flex items-center rounded-lg border border-border bg-background p-0.5"
        >
          {ANALYTICS_RANGES.map((option) => (
            <button
              key={option}
              type="button"
              data-testid={`analytics-range-${String(option)}`}
              aria-pressed={range === option}
              className={
                'rounded-md px-2.5 py-1 text-[12px] font-semibold tabular-nums transition-colors ' +
                (range === option
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground')
              }
              onClick={() => {
                switchRange(option);
              }}
            >
              {String(option)}
            </button>
          ))}
        </div>
        <button
          type="button"
          data-testid="analytics-refresh"
          aria-label={t('analytics.refresh_aria')}
          className="ml-auto grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
          disabled={session !== 'signed_in'}
          onClick={() => {
            refresh(range);
          }}
        >
          <RefreshCw aria-hidden className="size-4" />
        </button>
      </header>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[980px] px-4 py-6 sm:px-6 lg:px-8">
          {session === 'guest' ? (
            <div
              className="flex flex-col items-start gap-3 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between"
              data-testid="analytics-gate"
            >
              <div className="min-w-0">
                <p className="text-[14px] font-semibold text-foreground">
                  {t('analytics.gate.title')}
                </p>
                <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">
                  {t('analytics.gate.description')}
                </p>
              </div>
              <button
                type="button"
                data-testid="analytics-gate-signin"
                className="shrink-0 rounded-lg bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                onClick={() => {
                  void handleSignIn();
                }}
              >
                {t('calendar.gate.sign_in')}
              </button>
            </div>
          ) : load.kind === 'loading' ? (
            <div
              className="flex items-center gap-2 rounded-xl border border-border bg-card p-4 text-[13px] text-muted-foreground"
              data-testid="analytics-loading"
            >
              {t('common.loading')}
            </div>
          ) : load.kind === 'error' ? (
            <div
              role="alert"
              className="rounded-xl border border-border bg-card p-4 text-[13px] text-muted-foreground"
              data-testid="analytics-error"
            >
              {t('analytics.error')}
            </div>
          ) : summary === null ? (
            <div data-testid="analytics-loading">{t('common.loading')}</div>
          ) : isEmpty ? (
            <div
              className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-10 text-center"
              data-testid="analytics-empty"
            >
              <ClipboardList aria-hidden className="size-6 text-muted-foreground" />
              <p className="text-[14px] font-semibold text-foreground">
                {t('analytics.empty.title')}
              </p>
              <p className="text-[12.5px] text-muted-foreground">{t('analytics.empty.hint')}</p>
            </div>
          ) : (
            <>
              <div
                data-testid="analytics-totals"
                className="grid grid-cols-2 gap-3 sm:grid-cols-4"
              >
                <TotalsCard
                  testId="analytics-card-participant-minutes"
                  icon={<Timer aria-hidden className="size-3.5" />}
                  label={t('analytics.metrics.participant_minutes')}
                  value={totals?.participantMinutes ?? 0}
                  format={formatNumber}
                />
                <TotalsCard
                  testId="analytics-card-participant-sessions"
                  icon={<Users aria-hidden className="size-3.5" />}
                  label={t('analytics.metrics.participant_sessions')}
                  value={totals?.participantSessions ?? 0}
                  format={formatNumber}
                />
                <TotalsCard
                  testId="analytics-card-meetings-held"
                  icon={<Video aria-hidden className="size-3.5" />}
                  label={t('analytics.metrics.meetings_held')}
                  value={totals?.meetingsHeld ?? 0}
                  format={formatNumber}
                />
                <TotalsCard
                  testId="analytics-card-webinars-held"
                  icon={<VideoIcon aria-hidden className="size-3.5" />}
                  label={t('analytics.metrics.webinars_held')}
                  value={totals?.webinarsHeld ?? 0}
                  format={formatNumber}
                />
                <TotalsCard
                  testId="analytics-card-rooms-created"
                  icon={<CalendarPlus aria-hidden className="size-3.5" />}
                  label={t('analytics.metrics.rooms_created')}
                  value={totals?.roomsCreated ?? 0}
                  format={formatNumber}
                />
                <TotalsCard
                  testId="analytics-card-recordings"
                  icon={<Film aria-hidden className="size-3.5" />}
                  label={t('analytics.metrics.recordings_count')}
                  value={totals?.recordingsCount ?? 0}
                  format={formatNumber}
                />
                <TotalsCard
                  testId="analytics-card-invites"
                  icon={<ClipboardList aria-hidden className="size-3.5" />}
                  label={t('analytics.metrics.invites_created')}
                  value={totals?.invitesCreated ?? 0}
                  format={formatNumber}
                />
                <TotalsCard
                  testId="analytics-card-moderation-actions"
                  icon={<Users aria-hidden className="size-3.5" />}
                  label={t('analytics.metrics.moderation_actions')}
                  value={totals?.moderationActions ?? 0}
                  format={formatNumber}
                />
              </div>

              <div className="mt-4 grid gap-3 lg:grid-cols-2">
                <DailyBars
                  testId="analytics-chart-activity"
                  title={t('analytics.chart.activity')}
                  days={days}
                  value={(day) => day.participantMinutes}
                  label={dayLabel}
                  primaryClass="bg-sky-500"
                  secondaryClass="bg-transparent"
                />
                {/* The meetings-held split as stacked bars: meeting (sky) under webinar
                    (violet) per day - the CR-25 period split, no chart library needed. */}
                <DailyBars
                  testId="analytics-chart-meetings"
                  title={t('analytics.chart.meetings')}
                  days={days}
                  value={(day) => day.meetingsHeld + day.webinarsHeld}
                  split={(day) => ({ primary: day.meetingsHeld, secondary: day.webinarsHeld })}
                  label={dayLabel}
                  primaryClass="bg-sky-500"
                  secondaryClass="bg-violet-500"
                />
              </div>
              {/* The per-room substance: server-ordered (sessions desc) and capped rows for
                  the same range as the charts. */}
              <section
                aria-label={t('analytics.top.title')}
                data-testid="analytics-top"
                className="mt-4 rounded-xl border border-border bg-card p-4"
              >
                <h2 className="text-[13px] font-semibold text-foreground">
                  {t('analytics.top.title')}
                </h2>
                {summary.topRooms.length === 0 ? (
                  <p className="mt-3 text-[12.5px] text-muted-foreground" data-testid="analytics-top-empty">
                    {t('analytics.top.empty')}
                  </p>
                ) : (
                  <div className="scroll-thin mt-3 overflow-x-auto">
                    <table className="w-full border-collapse text-[12.5px]">
                      <thead>
                        <tr className="text-left text-[10.5px] uppercase tracking-wide text-muted-foreground">
                          <th scope="col" className="py-1.5 pr-3 font-semibold">
                            {t('analytics.top.col_room')}
                          </th>
                          <th scope="col" className="py-1.5 pr-3 font-semibold">
                            {t('analytics.top.col_kind')}
                          </th>
                          <th scope="col" className="py-1.5 pr-3 text-right font-semibold">
                            {t('analytics.top.col_sessions')}
                          </th>
                          <th scope="col" className="py-1.5 pr-3 text-right font-semibold">
                            {t('analytics.top.col_minutes')}
                          </th>
                          <th scope="col" className="py-1.5 pr-3 text-right font-semibold">
                            {t('analytics.top.col_recordings')}
                          </th>
                          <th scope="col" className="py-1.5 text-right font-semibold">
                            {t('analytics.top.col_last')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.topRooms.map((room) => (
                          <tr
                            key={room.roomSlug}
                            data-testid={`analytics-top-row-${room.roomSlug}`}
                            className="border-t border-border"
                          >
                            <td className="py-2 pr-3 font-medium text-foreground">{room.roomSlug}</td>
                            <td className="py-2 pr-3">
                              <KindBadge
                                kind={room.kind}
                                label={t(
                                  room.kind === 'webinar'
                                    ? 'analytics.top.kind_webinar'
                                    : 'analytics.top.kind_meeting',
                                )}
                              />
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums">
                              {formatNumber(room.sessions)}
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums">
                              {formatNumber(room.minutes)}
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums">
                              {formatNumber(room.recordings)}
                            </td>
                            <td className="py-2 text-right tabular-nums text-muted-foreground">
                              {lastActivityLabel(room.lastActivity)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
              <p
                data-testid="analytics-range-note"
                className="mt-3 text-[11.5px] text-muted-foreground"
              >
                {isAnalyticsRange(range)
                  ? t('analytics.range_note', { count: range })
                  : ''}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
